import React, { useMemo, useRef } from 'react';
import { ANSWERS } from '../content';
import { useFrameLoop, useMediaQuery } from '../../../hooks';

// The hero's artwork, in the place of the homepage's wave lines: twenty-eight hairlines
// read the context from the top, pinch through one pass and land on the outcomes, so each
// line is a twenty-eighth of the answer. Beads fall along them and take colour once they
// leave the pass.

const W = 600;
const H = 800;
const LINES = 28;
const TOP = -24;
const WAIST = 0.47 * H;
const FOOT = 0.82 * H;
// Lines this far apart never touch in the pass, and each outcome's lines keep the same
// spacing down to the foot, so the drawing stays lines rather than thickening into a band.
const GAP = 3.4;
const BEADS = 11;
const PERIOD = 4.8;

// Below 1280px the figure stands on its own beside or under the words, so it is framed
// closer and its labels are set larger to read at the smaller size.
const NARROW = '(max-width: 1279px)';
const VIEW = { wide: `0 0 ${W} ${H}`, narrow: '80 10 500 750' };
const LABEL_Y = { wide: [30, 48, 86], narrow: [33, 54, 94] };

interface Bundle { label: string; p: number; x: number; count: number }

const single = ANSWERS.find((a) => a.kind === 'single')!;
const team = single.outcomes;

// Lines per outcome, out of twenty-eight: each gets its whole share, and the lines left
// over go to the largest remainders, so the total holds and each small answer keeps one.
function shares(ps: number[], total: number) {
  const raw = ps.map((p) => p * total);
  const counts = raw.map(Math.floor);
  const order = raw.map((r, i) => [r - Math.floor(r), i]).sort((a, b) => b[0] - a[0]);
  const left = total - counts.reduce((a, b) => a + b, 0);
  for (let k = 0; k < left; k += 1) counts[order[k][1]] += 1;
  return counts;
}
const counts = shares(team.map(([, p]) => p), LINES);
const BUNDLES: Bundle[] = team.map(([label, p], i) => ({
  label,
  p,
  x: [0.4, 0.72, 0.9][i] * W,
  count: counts[i],
}));

interface Line { d: string; pts: [number, number][]; lens: number[]; bundle: number }

function cubic(p0: number[], p1: number[], p2: number[], p3: number[], n: number): [number, number][] {
  return Array.from({ length: n + 1 }, (_, i) => {
    const t = i / n;
    const m = 1 - t;
    return [
      m * m * m * p0[0] + 3 * m * m * t * p1[0] + 3 * m * t * t * p2[0] + t * t * t * p3[0],
      m * m * m * p0[1] + 3 * m * m * t * p1[1] + 3 * m * t * t * p2[1] + t * t * t * p3[1],
    ];
  });
}

function buildLines(): Line[] {
  const lines: Line[] = [];
  let i = 0;
  BUNDLES.forEach((b, bi) => {
    for (let k = 0; k < b.count; k += 1, i += 1) {
      // The fan is centred over the pass and stays inside the page's margin.
      const x0 = W * (0.17 + (0.76 * i) / (LINES - 1));
      const xw = W * 0.55 + (i - (LINES - 1) / 2) * GAP;
      const xb = b.x + (k - (b.count - 1) / 2) * GAP;
      const a = [[x0, TOP], [x0, TOP + (WAIST - TOP) * 0.55], [xw, WAIST - (WAIST - TOP) * 0.4], [xw, WAIST]];
      const c = [[xw, WAIST], [xw, WAIST + (FOOT - WAIST) * 0.4], [xb, FOOT - (FOOT - WAIST) * 0.55], [xb, FOOT]];
      const pts = cubic(a[0], a[1], a[2], a[3], 24).concat(cubic(c[0], c[1], c[2], c[3], 24).slice(1));
      const lens = [0];
      for (let j = 1; j < pts.length; j += 1) {
        lens.push(lens[j - 1] + Math.hypot(pts[j][0] - pts[j - 1][0], pts[j][1] - pts[j - 1][1]));
      }
      const d = `M${a[0].join(',')}C${a[1].join(',')} ${a[2].join(',')} ${a[3].join(',')}C${c[1].join(',')} ${c[2].join(',')} ${c[3].join(',')}`;
      lines.push({ d, pts, lens, bundle: bi });
    }
  });
  return lines;
}

// A point at fraction `u` of a line's length.
function at(line: Line, u: number): [number, number] {
  const total = line.lens[line.lens.length - 1];
  const target = total * u;
  let j = 1;
  while (j < line.lens.length - 1 && line.lens[j] < target) j += 1;
  const q = (target - line.lens[j - 1]) / (line.lens[j] - line.lens[j - 1] || 1);
  const [x0, y0] = line.pts[j - 1];
  const [x1, y1] = line.pts[j];
  return [x0 + (x1 - x0) * q, y0 + (y1 - y0) * q];
}

const hash = (k: number) => {
  const x = Math.sin(k * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};

// Two beads always take the smaller answers, so every outcome shows its colour as it lands;
// the others pick any line, so most land on billing, as most of the lines do. The two fall
// well apart, and at REST, the still frame for reduced motion, both have landed beside
// three billing beads (found by simulating the schedule).
const ALWAYS: Record<number, number> = { 2: 1, 9: 2 };
const REST = 3.3;

export function HeroLines() {
  const lines = useMemo(buildLines, []);
  const byBundle = useMemo(() => BUNDLES.map((_, b) => lines.filter((line) => line.bundle === b)), [lines]);
  const root = useRef<SVGSVGElement>(null);
  const beads = useRef<(SVGCircleElement | null)[]>([]);
  const narrow = useMediaQuery(NARROW);
  const [labelY, pY, questionY] = narrow ? LABEL_Y.narrow : LABEL_Y.wide;

  useFrameLoop(root, (t) => {
    beads.current.forEach((bead, j) => {
      if (!bead) return;
      const clock = t / PERIOD + j / BEADS;
      const phase = clock % 1;
      const turn = Math.floor(clock);
      const own = byBundle[ALWAYS[j]]?.length ? byBundle[ALWAYS[j]] : lines;
      const line = own[Math.floor(hash(j * 7.13 + turn * 3.7) * own.length)];
      // Falls for the first 82% of its turn, then rests where it landed and fades.
      const u = Math.min(1, phase / 0.82);
      const [x, y] = at(line, u);
      const landed = y > WAIST + 8; // colour once it has left the pass
      const fade = Math.min(1, phase / 0.06, (1 - phase) / 0.12);
      bead.setAttribute('cx', x.toFixed(1));
      bead.setAttribute('cy', y.toFixed(1));
      bead.setAttribute('r', landed ? '3.4' : '2.4');
      bead.setAttribute('opacity', fade.toFixed(2));
      bead.setAttribute('class', landed ? `oh-hero-bead is-landed-${line.bundle}` : 'oh-hero-bead');
    });
  }, REST);

  return (
    <svg
      ref={root}
      className={`oh-hero-lines${narrow ? ' is-narrow' : ''}`}
      viewBox={narrow ? VIEW.narrow : VIEW.wide}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <defs>
        {/* The lines fade in over the top fifth, so they stay faint behind the header. */}
        <linearGradient id="oh-strand-fade" gradientUnits="userSpaceOnUse" x1="0" y1={TOP} x2="0" y2={FOOT}>
          <stop offset="0" stopColor="currentColor" stopOpacity="0" />
          <stop offset="0.24" stopColor="currentColor" stopOpacity="1" />
          <stop offset="0.82" stopColor="currentColor" stopOpacity="1" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0.3" />
        </linearGradient>
      </defs>
      <g className="oh-hero-strands" stroke="url(#oh-strand-fade)">
        {lines.map((line, i) => <path key={i} d={line.d} />)}
      </g>
      {Array.from({ length: BEADS }, (_, j) => (
        <circle key={j} ref={(el) => { beads.current[j] = el; }} className="oh-hero-bead" r="2.4" cx="-10" cy="-10" />
      ))}
      <g className="oh-hero-labels">
        {BUNDLES.map((b, i) => (
          <g key={b.label} className={i === 0 ? 'is-top' : undefined}>
            <text className="oh-hero-label" x={b.x} y={FOOT + labelY} textAnchor="middle">{b.label}</text>
            <text className="oh-hero-p" x={b.x} y={FOOT + pY} textAnchor="middle">{b.p.toFixed(2)}</text>
          </g>
        ))}
        <text className="oh-hero-q" x={(BUNDLES[0].x + BUNDLES[BUNDLES.length - 1].x) / 2} y={FOOT + questionY} textAnchor="middle">
          {single.question}
        </text>
      </g>
    </svg>
  );
}

export default HeroLines;
